import { Box, Breadcrumbs, Container, Link as MuiLink, Typography } from '@mui/material';
import { LINK_PRIMIO, LINK_PRIMIO_DOCS } from '@main/config/links';
import { JsonLd } from '@presentation/seo/JsonLd';
import { buildAboutGraph, buildBreadcrumb } from '@presentation/seo/schema';

const linkSx = {
	color: (theme: { colors: { brand: string } }) => theme.colors.brand,
	textDecoration: 'none',
	'&:hover': { textDecoration: 'underline' },
} as const;

const sectionTitleSx = {
	fontSize: { xs: 20, md: 22 },
	fontWeight: 700,
	mb: 1.5,
	mt: 4,
	color: 'text.primary',
} as const;

const bodySx = {
	fontSize: { xs: 16, md: 17 },
	lineHeight: 1.7,
	color: 'text.primary',
	mb: 1.5,
	textWrap: 'pretty',
} as const;

const About = () => {
	const path = '/about';

	return (
		<Container maxWidth='md' sx={{ py: { xs: 3, md: 5 } }}>
			<JsonLd data={buildAboutGraph(path)} />
			<JsonLd data={buildBreadcrumb([{ name: 'Home', url: '/' }, { name: 'About' }])} />

			<Breadcrumbs sx={{ mb: 2, fontSize: 14 }}>
				<MuiLink href='/' sx={linkSx}>
					Home
				</MuiLink>
				<Typography component='span' sx={{ fontSize: 14, color: 'text.secondary' }}>
					About
				</Typography>
			</Breadcrumbs>

			<Typography component='h1' sx={{ fontSize: { xs: 26, md: 34 }, fontWeight: 800, mb: 2, textWrap: 'balance' }}>
				About Jigsaw Puzzle Universe
			</Typography>
			<Typography component='p' sx={{ ...bodySx, fontSize: { xs: 17, md: 19 }, mb: 0 }}>
				Jigsaw Puzzle Universe is a free digital jigsaw puzzle app for iOS and Android, with thousands of creator-made
				puzzles and new designs added every day. This website is the public catalogue — browse puzzles, creators, and
				categories, then open any design in the app to play.
			</Typography>

			<Box>
				<Typography component='h2' sx={sectionTitleSx}>
					Who builds it
				</Typography>
				<Typography component='p' sx={bodySx}>
					Jigsaw Puzzle Universe is built by{' '}
					<MuiLink href='https://www.gotiva.global' sx={linkSx}>
						Gotiva
					</MuiLink>
					, a product studio that designs and ships consumer apps. We care about calm, high-quality experiences —
					whether that means a quiet evening with a puzzle or the tools that help others build their own apps.
				</Typography>

				<Typography component='h2' sx={sectionTitleSx}>
					Also from our team
				</Typography>
				<Typography component='p' sx={bodySx}>
					Alongside Jigsaw Puzzle Universe, our team builds{' '}
					<MuiLink href={LINK_PRIMIO} sx={linkSx}>
						Primio
					</MuiLink>
					— an AI-powered platform that turns a plain-language description into a complete mobile app project. Primio
					generates real source code (not a drag-and-drop prototype) that compiles to native Android, iOS, and web apps,
					with a live preview, one-click store builds, and full source-code export so you keep ownership of what you
					create.
				</Typography>
				<Typography component='p' sx={bodySx}>
					To get started or dig into how it works, visit Primio or read the{' '}
					<MuiLink href={LINK_PRIMIO_DOCS} sx={linkSx}>
						Primio documentation
					</MuiLink>
					.
				</Typography>
			</Box>
		</Container>
	);
};

export default About;
